/**
 * Triggers element state once it becomes visible.
 * @typedef {(NodeList|HTMLCollection|String)=} Elements
 * @typedef {{offset: String, threshold: Number, delay: Number }=} Options
 */
export default class ScrollTrigger {
  /**
   * @param {Elements} elements
   * @param {Options} options
   */
  constructor(elements, options) {
    this._observers = []
    this._registeredElements = []

    if (elements) this.registerElements(elements, options)
  }

  /**
   * Register elements to observers
   * @param {(NodeList|HTMLCollection|String)} elements 
   * @param {{offset: String, threshold: Number, delay: Number }=} options
   */
  registerElements(elements, { offset = '0px', threshold = 0, delay = 0 } = {}) {
    const _elements = typeof elements === 'string' ? document.querySelectorAll(elements) : elements

    for (const el of _elements) {
      // Skip element if already registered by previous calls
      if ( this._registeredElements.includes(el) ) continue

      const rootMarginBottom = el.dataset.scrollOffset || offset
      const observerThreshold = parseFloat(el.dataset.scrollThreshold) || threshold
      const triggerDelay = parseFloat(el.dataset.scrollDelay) || delay

      const observerOptions = {
        rootMargin: `0px 0px ${rootMarginBottom} 0px`,
        threshold: observerThreshold
      }

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.dataset.scrollState = 1
            }, triggerDelay * 1000)

            observer.disconnect()
          }
        })
      }, observerOptions)

      observer.observe(el)

      this._observers.push(observer)
      this._registeredElements.push(el)
    }
  }

  /**
   * Disconnect all registered observers
   */
  clearObservers() {
    this._observers.forEach( observer => observer.disconnect() )
  }
}
